import { Box, Typography } from "@mui/material";
import { ErrorMessage, FormTextField } from "components";
import { Notice } from "components/Notice";
import { GetFormRules } from "lib/validation/formRules";
import * as labels from "./constants";
import { CardShell, ReadonlyField } from "./shared";

interface containmentInfoFieldNames {
	containmentName: string;
	description: string;
}

const defaultFieldNames: containmentInfoFieldNames = {
	containmentName: "containmentName",
	description: "description",
};

interface containmentInfoCardProps {
	readonly?: boolean;
	control?: any;
	name?: string;
	description?: string;
	mccId?: string;
	disabledFields?: {
		containmentName?: boolean;
		description?: boolean;
	};
	fieldNames?: Partial<containmentInfoFieldNames>;
	variant?: "default" | "nested";
}

export function ContainmentInfoCard({
	readonly = false,
	control,
	name,
	description,
	mccId,
	disabledFields,
	fieldNames: fieldNameOverrides,
	variant = "default",
}: containmentInfoCardProps): React.JSX.Element | null {
	const fields = { ...defaultFieldNames, ...fieldNameOverrides };

	if (readonly && !name && !description && !mccId) return null;

	return (
		<CardShell readonly={readonly} label={labels.ContainmentInformation} toolTip="Enter the containment name and description" variant={variant}>
			{readonly ? (
				<>
					{mccId && <ReadonlyField label={labels.MccId} value={mccId} />}
					{name && <ReadonlyField label={labels.ContainmentName} value={name} />}
					{description && <ReadonlyField label={labels.Description} value={description} />}
				</>
			) : (
				<>
					<Box data-test-selector="containment-name-field">
						<Typography sx={{ fontWeight: 600, fontSize: "0.8rem", mb: 0.5 }}>
							{labels.ContainmentName}
						</Typography>
						<FormTextField
							control={control!}
							name={fields.containmentName}
							label={labels.ContainmentName}
							disabled={disabledFields?.containmentName}
							rules={disabledFields?.containmentName ? undefined : GetFormRules("containmentName")}
							renderError={(errMsg) => <ErrorMessage msg={errMsg} />}
						/>
						{!disabledFields?.containmentName && (
							<Box sx={{ mt: 1.5 }}>
								<Notice
									message="Please note: Containment Name cannot be modified after containment has been created"
									delay={0}
								/>
							</Box>
						)}
					</Box>
					<Box data-test-selector="description-field">
						<Typography sx={{ fontWeight: 600, fontSize: "0.8rem", mb: 0.5 }}>
							{labels.Description}
						</Typography>
						<FormTextField
							control={control!}
							name={fields.description}
							label={labels.Description}
							disabled={disabledFields?.description}
							multiline
							minRows={3}
							maxRows={5}
							rules={disabledFields?.description ? undefined : GetFormRules("description")}
							renderError={(errMsg) => <ErrorMessage msg={errMsg} />}
						/>
					</Box>
				</>
			)}
		</CardShell>
	);
}
